import React, { useState, useEffect } from 'react';
import wikipediaService from '../services/wikipediaService';

const WikipediaCitation = ({ 
    metricKey, 
    stateCode = null, 
    compact = false,
    style = {} 
}) => {
    const [wikiInfo, setWikiInfo] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchWikiInfo = async () => {
            setLoading(true);
            try {
                // Check for metric-specific article first
                const metricArticle = await wikipediaService.checkMetricArticle(metricKey);
                
                // Also check for state-specific information if stateCode provided
                let stateArticle = null;
                if (stateCode) {
                    stateArticle = await wikipediaService.checkStateArticle(stateCode, metricKey);
                }

                setWikiInfo({
                    metric: metricArticle,
                    state: stateArticle
                });
            } catch (error) {
                console.error('Error fetching Wikipedia info:', error);
                setWikiInfo(null);
            } finally {
                setLoading(false);
            }
        };

        if (metricKey) {
            fetchWikiInfo();
        }
    }, [metricKey, stateCode]);

    if (loading) {
        return (
            <span style={{
                ...style,
                display: 'inline-flex',
                alignItems: 'center',
                padding: compact ? '2px 6px' : '4px 8px',
                fontSize: compact ? '11px' : '12px',
                color: '#666',
                backgroundColor: '#F0F0F0',
                borderRadius: '4px',
                cursor: 'default'
            }}>
                <span style={{ marginRight: '4px' }}>📖</span>
                Loading...
            </span>
        );
    }

    if (!wikiInfo || (!wikiInfo.metric && !wikiInfo.state)) {
        return null;
    }

    const primaryLink = wikiInfo.metric?.url || wikiInfo.state?.url;
    const primaryTitle = wikiInfo.metric?.title || wikiInfo.state?.title;

    if (compact) {
        // Compact button style for inline use
        return (
            <a
                href={primaryLink}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                    ...style,
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '3px 8px',
                    fontSize: '11px',
                    color: '#1E90FF',
                    backgroundColor: '#E8F4FD',
                    border: '1px solid #B0D4F1',
                    borderRadius: '12px',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#D0E8FA';
                    e.target.style.borderColor = '#90C4F1';
                }}
                onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#E8F4FD';
                    e.target.style.borderColor = '#B0D4F1';
                }}
                title={`View on Wikipedia: ${primaryTitle}`}
            >
                <span style={{ marginRight: '4px' }}>📖</span>
                Wiki
            </a>
        );
    }

    // Full citation card
    return (
        <div style={{
            ...style,
            padding: '12px',
            backgroundColor: '#F8FBFF',
            border: '1px solid #B0D4F1',
            borderRadius: '6px',
            fontSize: '12px'
        }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '8px',
                fontWeight: 'bold',
                color: '#1E90FF'
            }}>
                <span style={{ marginRight: '6px', fontSize: '16px' }}>📖</span>
                Wikipedia Citations Available
            </div>

            {wikiInfo.metric && (
                <div style={{ marginBottom: '8px' }}>
                    <div style={{ fontWeight: '500', marginBottom: '4px', color: '#333' }}>
                        Topic Articles:
                    </div>
                    {wikiInfo.metric.articles.map((article, index) => (
                        <a
                            key={index}
                            href={article.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                display: 'block',
                                padding: '4px 8px',
                                marginBottom: '4px',
                                backgroundColor: 'white',
                                border: '1px solid #E0E0E0',
                                borderRadius: '4px',
                                color: '#1E90FF',
                                textDecoration: 'none',
                                fontSize: '11px',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#F0F8FF';
                                e.target.style.borderColor = '#B0D4F1';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'white';
                                e.target.style.borderColor = '#E0E0E0';
                            }}
                        >
                            🔗 {article.title}
                        </a>
                    ))}
                </div>
            )}

            {wikiInfo.state && (
                <div>
                    <div style={{ fontWeight: '500', marginBottom: '4px', color: '#333' }}>
                        State Information:
                    </div>
                    <a
                        href={wikiInfo.state.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            display: 'block',
                            padding: '4px 8px',
                            backgroundColor: 'white',
                            border: '1px solid #E0E0E0',
                            borderRadius: '4px',
                            color: '#1E90FF',
                            textDecoration: 'none',
                            fontSize: '11px',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#F0F8FF';
                            e.target.style.borderColor = '#B0D4F1';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'white';
                            e.target.style.borderColor = '#E0E0E0';
                        }}
                    >
                        🔗 {wikiInfo.state.title}
                    </a>
                </div>
            )}

            <div style={{
                marginTop: '8px',
                padding: '6px',
                backgroundColor: '#E8F4FD',
                borderRadius: '4px',
                fontSize: '10px',
                color: '#666',
                textAlign: 'center'
            }}>
                ℹ️ Wikipedia data is community-edited and should be cross-referenced
            </div>
        </div>
    );
};

export default WikipediaCitation;